import React from 'react';
import {
  QrCode,
  ScanLine,
  Layers,
  History,
  LayoutTemplate,
  Wrench,
  Settings,
  Search,
  Smartphone,
  WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface HeaderProps {
  activeTab: string;
  onSelectTab: (tab: string) => void;
  onOpenCommandPalette: () => void;
  isOnline: boolean;
  canInstallPWA?: boolean;
  onInstallPWA?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  onSelectTab,
  onOpenCommandPalette,
  isOnline,
  canInstallPWA,
  onInstallPWA
}) => {
  const navItems = [
    { id: 'generator', label: 'Create', icon: <QrCode className="h-3.5 w-3.5" /> },
    { id: 'scanner', label: 'Scan', icon: <ScanLine className="h-3.5 w-3.5" /> },
    { id: 'batch', label: 'Batch', icon: <Layers className="h-3.5 w-3.5" /> },
    { id: 'history', label: 'History', icon: <History className="h-3.5 w-3.5" /> },
    { id: 'templates', label: 'Templates', icon: <LayoutTemplate className="h-3.5 w-3.5" /> },
    { id: 'tools', label: 'Tools', icon: <Wrench className="h-3.5 w-3.5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="h-3.5 w-3.5" /> }
  ];

  const categoryTitles: Record<string, string> = {
    generator: 'Create QR Code',
    scanner: 'QR Scanner',
    batch: 'Batch Generator',
    history: 'Saved History',
    templates: 'Design Templates',
    tools: 'Diagnostics & Tools',
    settings: 'Settings'
  };

  const isGeneratorTab = activeTab === 'generator';

  return (
    <header className="sticky top-0 z-40 w-full select-none">
      {/* Global Navigation Bar (Single combined bar on mobile) */}
      <div className="relative z-50 w-full bg-card/85 text-foreground backdrop-blur-md h-[48px] sm:h-[50px] border-b border-border flex items-center transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex items-center justify-between">
          {/* Brand & Mobile View Title */}
          <div
            onClick={() => onSelectTab('generator')}
            className="flex items-center gap-2.5 cursor-pointer text-foreground hover:text-primary transition-colors"
          >
            <div className="p-1.5 rounded-lg bg-primary text-primary-foreground shadow-xs">
              <QrCode className="h-4 w-4" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
              <span className="text-[15px] font-semibold tracking-[-0.28px]">QR Studio</span>
              {/* On mobile, show current view title right in top bar */}
              <span className="text-[12px] text-muted-foreground sm:hidden font-normal">
                &bull; {categoryTitles[activeTab] || 'Create'}
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-6 text-[13px] tracking-[-0.12px]">
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onSelectTab(item.id)}
                  className={`transition-colors cursor-pointer ${
                    isActive
                      ? 'text-primary font-semibold'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Right Tools: Search & Offline status */}
          <div className="flex items-center gap-2 sm:gap-3">
            {!isOnline && (
              <div className="flex items-center gap-1 text-[11px] text-warning bg-warning/10 px-2 py-0.5 rounded-full">
                <WifiOff className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}

            {canInstallPWA && onInstallPWA && (
              <button
                onClick={onInstallPWA}
                className="hidden sm:flex items-center gap-1 text-[12px] text-primary hover:bg-primary/10 px-2.5 py-1 rounded-full border border-primary/30 transition-colors"
              >
                <Smartphone className="h-3.5 w-3.5" />
                <span>Install</span>
              </button>
            )}

            {!isGeneratorTab && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onSelectTab('generator')}
                className="hidden sm:inline-flex text-[12px] py-1 h-[30px] px-3.5"
              >
                New QR
              </Button>
            )}

            <button
              onClick={onOpenCommandPalette}
              className="text-muted-foreground hover:text-foreground p-1.5 rounded-full hover:bg-muted transition-colors cursor-pointer flex items-center gap-1 text-[12px]"
              title="Search (⌘K)"
            >
              <Search className="h-4 w-4" />
              <kbd className="hidden lg:inline text-[10px] font-mono px-1 bg-muted rounded">⌘K</kbd>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
